import React from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ToastProvider } from './contexts/ToastContext';
import { ConfirmProvider } from './contexts/ConfirmContext';
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';
import ClientRoute from './components/ClientRoute';
import Layout from './components/Layout';

// Pages
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import ConfirmResetPassword from './pages/ConfirmResetPassword';
import Dashboard from './pages/Dashboard';

// Admin Pages
import ClientsManagement from './pages/admin/ClientsManagement';
import AddClient from './pages/admin/AddClient';
import EditClient from './pages/admin/EditClient';
import ClientSites from './pages/admin/ClientSites';
import SitesManagement from './pages/admin/SitesManagement';
import EquipmentManagement from './pages/admin/EquipmentManagement';
import AddExtinguisher from './pages/admin/AddExtinguisher';
import AddOtherEquipment from './pages/admin/AddOtherEquipment';
import EditEquipment from './pages/admin/EditEquipment';
import PlanManagement from './pages/admin/PlanManagement';
import PlaceEquipment from './pages/admin/PlaceEquipment';
import MaintenanceManagement from './pages/admin/MaintenanceManagement';
import Reports from './pages/admin/Reports';
import ReportPreview from './pages/admin/ReportPreview';
import AdminProfile from './pages/admin/AdminProfile';
import AdminProfileBackups from './pages/admin/AdminProfileBackups';
import AdminProfileOutlet from './components/AdminProfileOutlet';
import CabinetPlacements from './pages/admin/CabinetPlacements';
import VehicleExtinguishers from './pages/admin/VehicleExtinguishers';
import ExtinguisherInventory from './pages/admin/ExtinguisherInventory';

// Client Pages
import ClientEquipmentList from './pages/client/EquipmentList';
import EquipmentDetails from './pages/client/EquipmentDetails';
import InteractivePlan from './pages/client/InteractivePlan';
import ClientReports from './pages/client/Reports';
import Notifications from './pages/client/Notifications';
import ClientProfile from './pages/client/ClientProfile';
import CabinetPlacementsView from './pages/client/CabinetPlacementsView';
import VehicleEquipmentView from './pages/client/VehicleEquipmentView';
import ExtinguisherInventoryView from './pages/client/ExtinguisherInventoryView';

function App() {
  return (
    <Router>
      <AuthProvider>
        <NotificationProvider>
          <ToastProvider>
          <ConfirmProvider>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/reset-password/confirm" element={<ConfirmResetPassword />} />

            {/* Protected Routes */}
            <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />

              {/* Admin Routes */}
              <Route path="clients" element={<AdminRoute><ClientsManagement /></AdminRoute>} />
              <Route path="clients/add" element={<AdminRoute><AddClient /></AdminRoute>} />
              <Route path="clients/edit/:id" element={<AdminRoute><EditClient /></AdminRoute>} />
              <Route path="clients/:clientId/sites" element={<AdminRoute><ClientSites /></AdminRoute>} />
              <Route path="admin/sites" element={<AdminRoute><SitesManagement /></AdminRoute>} />
              <Route path="admin/sites/:siteId/equipment" element={<AdminRoute><EquipmentManagement /></AdminRoute>} />
              <Route path="equipment" element={<AdminRoute><EquipmentManagement /></AdminRoute>} />
              <Route path="equipment/add-extinguisher" element={<AdminRoute><AddExtinguisher /></AdminRoute>} />
              <Route path="equipment/add-other" element={<AdminRoute><AddOtherEquipment /></AdminRoute>} />
              <Route path="equipment/edit/:id" element={<AdminRoute><EditEquipment /></AdminRoute>} />
              <Route path="plans" element={<AdminRoute><PlanManagement /></AdminRoute>} />
              <Route path="plans/place/:clientId" element={<AdminRoute><PlaceEquipment /></AdminRoute>} />
              <Route path="maintenance" element={<AdminRoute><MaintenanceManagement /></AdminRoute>} />
              <Route path="reports" element={<AdminRoute><Reports /></AdminRoute>} />
              <Route path="reports/preview" element={<AdminRoute><ReportPreview /></AdminRoute>} />
              <Route
                path="admin-profile"
                element={
                  <AdminRoute>
                    <AdminProfileOutlet />
                  </AdminRoute>
                }
              >
                <Route index element={<AdminProfile />} />
                <Route path="backups" element={<AdminProfileBackups />} />
              </Route>
              <Route path="admin/cabinet-placements" element={<AdminRoute><CabinetPlacements /></AdminRoute>} />
              <Route path="admin/vehicle-equipment" element={<AdminRoute><VehicleExtinguishers /></AdminRoute>} />
              <Route path="admin/extinguisher-inventory" element={<AdminRoute><ExtinguisherInventory /></AdminRoute>} />
              <Route path="admin/messages" element={<Navigate to="/admin-profile?tab=messages" replace />} />
              <Route path="admin/backups" element={<Navigate to="/admin-profile/backups" replace />} />

              {/* Client Routes */}
              <Route path="my-sites" element={<ClientRoute><ClientEquipmentList /></ClientRoute>} />
              <Route path="my-sites/:siteId/equipment" element={<ClientRoute><ClientEquipmentList /></ClientRoute>} />
              <Route path="my-equipment/:id" element={<ClientRoute><EquipmentDetails /></ClientRoute>} />
              <Route path="my-plan" element={<ClientRoute><InteractivePlan /></ClientRoute>} />
              <Route path="my-reports" element={<ClientRoute><ClientReports /></ClientRoute>} />
              <Route path="profile" element={<ClientRoute><ClientProfile /></ClientRoute>} />
              <Route path="technical/cabinet-placements" element={<ClientRoute><CabinetPlacementsView /></ClientRoute>} />
              <Route path="technical/vehicle-equipment" element={<ClientRoute><VehicleEquipmentView /></ClientRoute>} />
              <Route path="technical/extinguisher-inventory" element={<ClientRoute><ExtinguisherInventoryView /></ClientRoute>} />

              {/* Shared (both roles) */}
              <Route path="notifications" element={<Notifications />} />
            </Route>

            {/* 404 */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </ConfirmProvider>
          </ToastProvider>
        </NotificationProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;

